import { motion } from 'framer-motion';
import { useComputedColorScheme } from '@mantine/core';

export default function StablyLoader({
  size = 125,
  colorOverride,
  className = '',
  cycleDuration = 2,
}: {
  size?: number;
  strokeWidth?: number;
  colorOverride?: string;
  className?: string;
  cycleDuration?: number;
}) {
  const colorScheme = useComputedColorScheme();
  const isDark = colorScheme === 'dark';

  const color = colorOverride || (isDark ? 'white' : 'white'); // for now, white also looks good for light mode

  // Set sizes and positions
  const viewBoxSize = size / 1.25;
  const center = viewBoxSize / 2;
  const strokeWidth = size / 12.5;
  const radius = center - strokeWidth * 2;
  const innerRadius = radius * 0.6;

  // Animation variants
  const innerCircleVariants = {
    initial: {
      strokeDasharray: `${2 * Math.PI * innerRadius}`,
      strokeDashoffset: `${2 * Math.PI * innerRadius}`,
      rotate: 180,
    },
    animate: {
      strokeDashoffset: `${Math.PI * innerRadius}`,
      rotate: [180, -180],
      transition: {
        strokeDashoffset: {
          duration: cycleDuration / 2, // Half of the cycle for one direction
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'reverse',
        },
        rotate: {
          duration: cycleDuration, // Full cycle for rotation
          ease: 'linear',
          repeat: Infinity,
          repeatType: 'loop',
        },
      },
    },
  };

  return (
    <div
      className={`flex flex-col items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        initial="initial"
        animate="animate"
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
        />

        {/* Outer animated circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Inner animated circle */}
        <motion.circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          variants={innerCircleVariants}
          className="origin-center"
        />
      </motion.svg>
    </div>
  );
}
